'use client';

import * as React from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons';

import { Icon } from './icon.component';

import { cn } from '@/utils/cn-tw-merge';

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => <AccordionPrimitive.Item ref={ref} className={cn(className)} {...props} />);
AccordionItem.displayName = 'AccordionItem';

interface AccordionTriggerProps extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> {
  showNoIcon?: boolean;
  startIcon?: boolean;
  openedStyle?: string;
  underlineOnHover?: boolean;
  classNameIcon?: string;
}

const AccordionTrigger = React.forwardRef<React.ElementRef<typeof AccordionPrimitive.Trigger>, AccordionTriggerProps>(
  (
    {
      className,
      children,
      showNoIcon = false,
      startIcon = false,
      openedStyle,
      underlineOnHover = true,
      classNameIcon,
      ...props
    },
    ref,
  ) => {
    const hasOpenedStyle = Boolean(openedStyle);

    return (
      <AccordionPrimitive.Header className="flex">
        <AccordionPrimitive.Trigger
          ref={ref}
          className={cn(
            'flex flex-1 items-center justify-between font-medium transition-all  [&[data-state=open]>#chevron]:-rotate-180',
            underlineOnHover ? 'hover:underline' : 'hover:no-underline',
            className,
            hasOpenedStyle ? openedStyle : undefined,
          )}
          {...props}
        >
          {startIcon && (
            <Icon
              id="chevron"
              icon={faChevronDown}
              className="h-4 w-4 mr-2 shrink-0 transition-transform duration-200"
            />
          )}
          {children}
          {!showNoIcon && (
            <Icon
              id="chevron"
              icon={faChevronDown}
              className={cn('h-5 w-5 shrink-0 transition-transform duration-200', classNameIcon)}
            />
          )}
        </AccordionPrimitive.Trigger>
      </AccordionPrimitive.Header>
    );
  },
);
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className="overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up   data-[state=open]:animate-accordion-down"
    {...props}
  >
    <div className={cn(className)}>{children}</div>
  </AccordionPrimitive.Content>
));

AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
